import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactConfetti from 'react-confetti';
import { Helmet } from 'react-helmet';

const HIGHLIGHT_COLORS = ['bg-yellow-200', 'bg-pink-200', 'bg-blue-200', 'bg-orange-200', 'bg-green-200', 'bg-purple-200'];

const VictoryModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg text-center relative z-60">
        <h3 className="text-2xl font-bold mb-4">Congratulations!</h3>
        <p className="mb-6">You've found all the words!</p>
        <button
          onClick={onClose}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Play Again
        </button>
      </div>
    </div>
  );
};

const WordSearch = () => {
  const [puzzle, setPuzzle] = useState({ letters: [], words: [] });
  const [foundWords, setFoundWords] = useState([]);
  const [highlightedCells, setHighlightedCells] = useState([]);
  const [tempHighlightedCells, setTempHighlightedCells] = useState([]);
  const [startCell, setStartCell] = useState(null);
  const [currentCell, setCurrentCell] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isGameWon, setIsGameWon] = useState(false);
  const [confettiDimensions, setConfettiDimensions] = useState({ width: 0, height: 0 });
  const gridRef = useRef(null);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data/word_search.json`)
      .then(response => response.json())
      .then(data => setPuzzle(data));
  }, []);

  useEffect(() => {
    // Check if all words are found
    if (foundWords.length === puzzle.words.length && puzzle.words.length > 0) {
      setIsGameWon(true);
    }
  }, [foundWords, puzzle.words]);

  useEffect(() => {
    // Set confetti dimensions on mount
    setConfettiDimensions({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  const getSelectedWord = useCallback((start, end) => {
    const cells = getCellsBetween(start, end);
    return cells.map(cell => {
      if (cell.i >= 0 && cell.i < puzzle.letters.length &&
        cell.j >= 0 && cell.j < puzzle.letters[0].length) {
        return puzzle.letters[cell.i][cell.j];
      }
      return '';
    }).join('');
  }, [puzzle.letters]);

  const checkSelectedWord = useCallback(() => {
    if (startCell && currentCell) {
      const selectedWord = getSelectedWord(startCell, currentCell);
      const normalizedSelectedWord = normalizeWord(selectedWord);
      const matchedWord = puzzle.words.find(word => normalizeWord(word) === normalizedSelectedWord);
      if (matchedWord && !foundWords.includes(matchedWord)) {
        const colorClass = HIGHLIGHT_COLORS[Math.floor(Math.random() * HIGHLIGHT_COLORS.length)];
        setFoundWords(prev => [...prev, matchedWord]);
        setHighlightedCells(prev => [
          ...prev,
          ...getCellsBetween(startCell, currentCell).map(cell => ({ ...cell, word: matchedWord, colorClass }))
        ]);
      }
    }
  }, [startCell, currentCell, getSelectedWord, puzzle.words, foundWords]);


  useEffect(() => {
    const handleGlobalEnd = () => {
      if (isSelecting) {
        checkSelectedWord();
      }
      setIsSelecting(false);
      setStartCell(null);
      setCurrentCell(null);
      setTempHighlightedCells([]);
    };

    window.addEventListener('mouseup', handleGlobalEnd);
    window.addEventListener('touchend', handleGlobalEnd);
    return () => {
      window.removeEventListener('mouseup', handleGlobalEnd);
      window.removeEventListener('touchend', handleGlobalEnd);
    };
  }, [isSelecting, checkSelectedWord]);

  useEffect(() => {
    const grid = gridRef.current;
    if (grid) {
      const preventDefault = (e) => {
        e.preventDefault();
      };
      grid.addEventListener('touchmove', preventDefault, { passive: false });
      return () => {
        grid.removeEventListener('touchmove', preventDefault);
      };
    }
  }, []);

  const handleStart = (i, j) => {
    setIsSelecting(true);
    setStartCell({ i, j });
    setCurrentCell({ i, j });
    setTempHighlightedCells([{ i, j }]);
  };

  const handleMove = (i, j) => {
    if (isSelecting) {
      setCurrentCell({ i, j });
      setTempHighlightedCells(getCellsBetween(startCell, { i, j }));
    }
  };

  const getCellsBetween = (start, end) => {
    if (!start || !end) return [];
    const cells = [];
    const dx = Math.sign(end.i - start.i);
    const dy = Math.sign(end.j - start.j);
    let { i, j } = start;
    while (i !== end.i + dx || j !== end.j + dy) {
      cells.push({ i, j });
      i += dx;
      j += dy;
      if (cells.length > 100) break; // Safeguard against infinite loops
    }
    return cells;
  };

  const normalizeWord = (word) => {
    return word.replace(/[^a-zA-Z]/g, '').toLowerCase();
  };

  const getCellClass = (i, j) => {
    const highlightedCell = highlightedCells.find(cell => cell.i === i && cell.j === j);
    if (highlightedCell) {
      return highlightedCell.colorClass;
    }
    if (tempHighlightedCells.some(cell => cell.i === i && cell.j === j)) {
      return 'bg-gray-200';
    }
    return '';
  };

  const getCellAriaLabel = (i, j) => {
    const letter = puzzle.letters[i][j];
    const highlightedCell = highlightedCells.find(cell => cell.i === i && cell.j === j);
    const selected = highlightedCell ? 'selected' : 'not selected';
    return `Letter ${letter} at row ${i + 1}, column ${j + 1}, ${selected}`;
  };

  return (
    <>
      <Helmet>
        <title>Word Search | Project V.O.T.E.</title>
      </Helmet>
      <div className="mx-auto max-w-7xl sm:px-4 md:px-6 lg:px-8 h-full">
        <div className="mx-auto max-w-7xl bg-white p-3 rounded-t-lg">
          <h1 className="mb-4 text-2xl font-bold">Word Search</h1>

          <div className="max-w-2xl">

            <div className="py-4 overflow-x-auto">
              <div
                ref={gridRef}
                className="select-none grid md:gap-1 border border-gray-300"
                role="grid"
                aria-label="Word search puzzle"
                style={{
                  gridTemplateColumns: `repeat(${puzzle.letters[0]?.length || 0}, minmax(20px, 1fr))`, // Ensure the correct number of columns
                }}
              >
                {puzzle.letters.map((row, i) => (
                  <React.Fragment key={i}>
                    {row.map((letter, j) => (
                      <div
                        key={`${i}-${j}`}
                        className={`cell font-bold text-md md:text-2xl aspect-square flex items-center justify-center cursor-pointer ${getCellClass(i, j)}`}
                        onMouseDown={() => handleStart(i, j)}
                        onMouseEnter={() => handleMove(i, j)}
                        onTouchStart={(e) => { e.preventDefault(); handleStart(i, j); }}
                        onTouchMove={(e) => {
                          e.preventDefault();
                          const touch = e.touches[0];
                          const element = document.elementFromPoint(touch.clientX, touch.clientY);
                          const cellIndex = element?.dataset?.index;
                          if (cellIndex) {
                            const [newI, newJ] = cellIndex.split('-').map(Number);
                            handleMove(newI, newJ);
                          }
                        }}
                        data-index={`${i}-${j}`}
                        aria-label={getCellAriaLabel(i, j)}
                        role="gridcell"
                      >
                        {letter}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>



            <div className="p-4">
              <h2 className="text-lg font-bold mb-2">Words to Find:</h2>
              <ul className='flex flex-wrap'>
                {puzzle.words.map((word) => (
                  <li
                    key={word}
                    className={`mr-4 mb-2 ${foundWords.includes(word) ? 'line-through text-gray-500' : ''}`}
                    aria-label={`Word ${word} ${foundWords.includes(word) ? 'found' : 'not found'}`}
                  >
                    {word}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {isGameWon && (
          <>
            <VictoryModal onClose={() => window.location.reload()} />
            <ReactConfetti
              style={{ position: 'fixed', top: 0, left: 0, zIndex: 100 }}
              width={confettiDimensions.width}
              height={confettiDimensions.height}
              recycle={false}
              numberOfPieces={200}
            />
          </>
        )}
      </div>
    </>
  );
};

export default WordSearch;
